import React, { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import Routes from '../../constants/routes';
import cookies from '../../utils/browser-storage/cookies';
import { storageKeys } from '../../utils/browser-storage/storage-keys';
import schoolAccount from '../../utils/school-account';
import session from '../../utils/session';

export enum ROLE {
    GUEST = 'guest',
    USER = 'user',
    MEMBER = 'member',
    MASTER = 'master',
    AUTHOR = 'author'
}

type AccessLayerType = {
    hasAccess: boolean;
    redirect: string;
};

type AccessLayersType = {
    [key: string]: AccessLayerType;
};

type PrivateRoutePropsType = {
    access?: ROLE | ROLE[];
};

const PrivateRoute = ({ children, access = ROLE.USER }: PropsWithChildren<PrivateRoutePropsType>) => {
    const location = useLocation();
    const user = useSelector((state: RootState) => state.main.user);
    const accessLayers: AccessLayersType = {
        [ROLE.GUEST]: {
            hasAccess: !session.isLoggedIn(),
            redirect: Routes.HOME.path
        },
        [ROLE.USER]: {
            hasAccess: session.isLoggedIn(),
            redirect: Routes.LOGIN.path
        },
        [ROLE.AUTHOR]: {
            hasAccess: user?.details?.totalMaterials > 0,
            redirect: Routes.SELLER.path
        },
        [ROLE.MEMBER]: {
            hasAccess: Boolean(schoolAccount.isVerifiedUser(user)),
            redirect: Routes.MY_SCHOOL.path
        },
        [ROLE.MASTER]: {
            hasAccess: schoolAccount.isAdmin(user),
            redirect: Routes.MY_SCHOOL.path
        }
    };

    if (!user.loaded) {
        return null;
    }

    if (typeof access === 'string') {
        const accessLayer: AccessLayerType = accessLayers[access];

        if (!accessLayer.hasAccess) {
            cookies.setItem(storageKeys.general.redirectLink, location.pathname, 1);

            return <Navigate to={accessLayer.redirect} replace />;
        }

        return <>{children}</>;
    }

    const userAccesses = Object.keys(accessLayers).filter((layer) => accessLayers[layer].hasAccess);

    if (!access.find((layer) => userAccesses.includes(layer))) {
        cookies.setItem(storageKeys.general.redirectLink, location.pathname, 1);

        return <Navigate to={Routes.HOME.path} replace />;
    }

    return <>{children}</>;
};

export default PrivateRoute;
